import React, { useState, useEffect, useMemo } from 'react';
import { 
    Card, 
    Select, 
    Option, 
    Button, 
    Variants, 
    Label,
    Heading,
    Grid,
    TextTypes,
    SingleDatePicker,
    SinglePickerValueType,
    Toggle
} from '@sede-enterprise/shell-ds-react-framework';
import ExcelJS from 'exceljs';
import { useNavigate } from 'react-router-dom';
import { Col } from '../../components/flexbox/Flex';
import { ContainerDiv } from '../../components/styledComponent/StyledComponent';
import Loader from '../../components/loader/Loader';
import { useToast } from 'rc-toastr';
import { usePageTitle } from '../../services/UtilService';
import { useTranslation } from 'react-i18next';
import { getData, postData } from '../../services/ApiService';
import { ApiUrls } from '../../helpers/ApiUrls';
import dayjs from 'dayjs';
import './styles.css';

interface ReportConfig {
    id: string;
    reportName: string;
    reportDescription: string;
    filterCriteria: string;
    filterCriteriaValue: string;
    filterCriteriaId?: number;
    selectedGroups: string[];
    selectedColumns: string[];
    isActive: boolean;
    isStatic?: boolean;
    staticReportId?: number | null;
}

interface ReportListResponseItem {
    id: number;
    reportName: string;
    reportDescription: string;
    filterCriteria: string;
    filterCriteriaValue: string;
    filterCriteriaId?: number;
    selectedGroups: string;
    selectedColumns: string;
    isActive: boolean;
    isStatic?: boolean | number | null;
    staticReportId?: number | null;
}

interface FilterValueOption {
    id: string;
    name: string;
    value?: string | null;
}

interface MasterDataItem {
    id: number;
    masterType: string;
    fieldName: string;
    displayName: string;
}

interface ColumnDefinition {
    name: string;
    type: string;
    style: string;
}

interface RowStyle {
    rowIndex: number;
    style: string;
}

interface GroupData {
    group: string;
    groupStyle: string;
    rowCount: string;
    rows: string[][];
    rowStyles: RowStyle[];
    groupLabel?: string;
    groupDisplayName?: string;
}

interface ReportResponse {
    dateType?: string;
    startDate?: string;
    endDate?: string;
    report: {
        metadata: {
            title: string;
            file: string;
            filter: string;
            filterStyle: string;
            dateType?: string;
            startDate?: string;
            endDate?: string;
        };
        data: {
            columns: ColumnDefinition[];
            groups: GroupData[];
        };
    };
}

const GenerateReport = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { t } = useTranslation();
    usePageTitle(t('shellAviationAIMS') + " | Generate Report");

    const [reports, setReports] = useState<ReportConfig[]>([]);
    const [selectedReportId, setSelectedReportId] = useState<string>('');
    const [selectedReport, setSelectedReport] = useState<ReportConfig | null>(null);
    const [filterCriteriaValue, setFilterCriteriaValue] = useState<string[]>([]);
    const [dateTimeType, setDateTimeType] = useState<string>('TRANSACTION_DATE');
    const [startDate, setStartDate] = useState<SinglePickerValueType>(null);
    const [endDate, setEndDate] = useState<SinglePickerValueType>(null);
    const [passthroughIncluded, setPassthroughIncluded] = useState<boolean>(true);

    const endPickerDefaultValue = useMemo(() => {
        if (!startDate || endDate) return undefined;
        return dayjs(startDate);
    }, [startDate, endDate]);

    const [countries, setCountries] = useState<{id: string, name: string}[]>([]);
    const [airports, setAirports] = useState<{id: string, name: string, countryCode: string}[]>([]);
    const [customers, setCustomers] = useState<{id: string, name: string}[]>([]);
    const [paymentTypes, setPaymentTypes] = useState<FilterValueOption[]>([]);
    const [isLoadingFilters, setIsLoadingFilters] = useState(false);
    const [filterOptions, setFilterOptions] = useState<MasterDataItem[]>([]);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);

    const shouldShowFilterFields = Boolean(selectedReport && !selectedReport.isStatic);


    const parseListField = (value?: string | null): string[] => {
        if (!value) return [];
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return parsed
                    .map((entry) => (typeof entry === 'string' ? entry.trim() : String(entry)))
                    .filter((entry) => entry.length > 0);
            }
        } catch (error) {
            // Ignore JSON parse errors and fall back to comma-separated parsing
        }

        return value
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => entry.length > 0);
    };

    const serializeListField = (values: string[]): string | null => {
        if (!values || values.length === 0) return null;
        return values.join(',');
    };

    // Load reports and filter options
    useEffect(() => {
        const reportListUrl = `${ApiUrls.reportList}?searchTerm=`;
        getData(reportListUrl)
            .then((response) => {
                const reportItems: ReportListResponseItem[] = response.data?.data || [];
                const mappedReports: ReportConfig[] = reportItems
                    .filter((item) => item.isActive)
                    .map((item) => {
                        const isStaticFlag = item.isStatic === true || item.isStatic === 1 || item.isStatic === '1';
                        const staticReportId =
                            item.staticReportId === null || item.staticReportId === undefined
                                ? null
                                : Number(item.staticReportId);
                        return {
                            id: item.id.toString(),
                            reportName: item.reportName,
                            reportDescription: item.reportDescription,
                            filterCriteria: item.filterCriteria,
                            filterCriteriaValue: item.filterCriteriaValue ?? '',
                            filterCriteriaId: item.filterCriteriaId,
                            selectedGroups: parseListField(item.selectedGroups),
                            selectedColumns: parseListField(item.selectedColumns),
                            isActive: item.isActive,
                            isStatic: isStaticFlag,
                            staticReportId
                        };
                    });
                setReports(mappedReports);
            })
            .catch((error) => {
                console.error('Failed to load reports:', error);
                toast.error('Failed to load reports');
            });

        // Load filter options and master data
        setIsLoadingFilters(true);
        Promise.all([
            getData(ApiUrls.reportMasterData),
            getData(ApiUrls.countries),
            getData(ApiUrls.airports),
            getData(ApiUrls.customers),
            getData(ApiUrls.paymentTypes)
        ])
        .then(([masterDataResponse, countriesResponse, airportsResponse, customersResponse, paymentTypesResponse]) => {
            const masterData = masterDataResponse.data?.data ?? masterDataResponse.data ?? [];
            const filters = (masterData as MasterDataItem[]).filter((item) => item.masterType === 'F');
            setFilterOptions(filters);

            const countriesData = countriesResponse.data?.data ?? countriesResponse.data ?? [];
            const airportsData = airportsResponse.data?.data ?? airportsResponse.data ?? [];
            const customersData = customersResponse.data?.data ?? customersResponse.data ?? [];
            const paymentTypesData = paymentTypesResponse.data?.data ?? paymentTypesResponse.data ?? [];
            setCountries(countriesData);
            setAirports(airportsData);
            setCustomers(customersData);
            setPaymentTypes(
                (Array.isArray(paymentTypesData) ? paymentTypesData : []).map((item: { paymentTypeCd: string; paymentType: string }) => ({
                    id: item.paymentTypeCd,
                    name: item.paymentType,
                    value: item.paymentTypeCd
                }))
            );
            setIsLoadingFilters(false);
        })
        .catch((error) => {
            console.error('Failed to load filter options:', error);
            toast.error('Failed to load filter options');
            setIsLoadingFilters(false);
        });
    }, []);

    // When report is selected, load its configuration
    useEffect(() => {
        if (selectedReportId) {
            const report = reports.find(r => r.id === selectedReportId);
            if (report) {
                setSelectedReport(report);
                setFilterCriteriaValue(report.isStatic ? [] : parseListField(report.filterCriteriaValue));
            }
        } else {
            setSelectedReport(null);
            setFilterCriteriaValue([]);
            setDateTimeType('TRANSACTION_DATE');
        }
    }, [selectedReportId, reports]);

    const isFilterValueRequired = useMemo(() => {
        if (!selectedReport || selectedReport.isStatic) return false;
        const selectedFilter = filterOptions.find((option) => option.displayName === selectedReport.filterCriteria);
        const fieldName = (selectedFilter?.fieldName || selectedReport.filterCriteria || '').toLowerCase();
        const displayName = (selectedFilter?.displayName || selectedReport.filterCriteria || '').toLowerCase();
        return !(fieldName.includes('international') || fieldName.includes('intl') || displayName.includes('international'));
    }, [selectedReport, filterOptions]);

    const convertColorToHex = (color: string): string | null => {
        if (!color) return null;
        
        // If already hex, return with FF for alpha
        if (color.startsWith('#')) {
            const hex = color.substring(1);
            return 'FF' + hex.toUpperCase().padEnd(6, '0');
        }

        // Color name to hex mapping
        const colorMap: { [key: string]: string } = {
            'red': 'FFFF0000',
            'green': 'FF008000',
            'blue': 'FF0000FF',
            'yellow': 'FFFFFF00',
            'black': 'FF000000',
            'white': 'FFFFFFFF',
            'gray': 'FF808080',
            'grey': 'FF808080',
            'orange': 'FFFFA500',
            'purple': 'FF800080',
            'pink': 'FFFFC0CB'
        };

        return colorMap[color.toLowerCase()] || null;
    };

    const parseStyleString = (styleString: string): Partial<ExcelJS.Style> => {
        const style: Partial<ExcelJS.Style> = {};
        if (!styleString) return style;

        styleString.split(';').forEach((declaration) => {
            const parts = declaration.split(':');
            if (parts.length < 2) return;
            const property = parts[0].trim().toLowerCase();
            const value = parts.slice(1).join(':').trim();
            if (!property || !value) return;

            switch (property) {
                case 'font-weight':
                    if (!style.font) style.font = {};
                    style.font.bold = value === 'bold';
                    break;
                case 'font-style':
                    if (!style.font) style.font = {};
                    style.font.italic = value === 'italic';
                    break;
                case 'color': {
                    if (!style.font) style.font = {};
                    const colorHex = convertColorToHex(value);
                    if (colorHex) style.font.color = { argb: colorHex };
                    break;
                }
                case 'background-color': {
                    const bgColorHex = convertColorToHex(value);
                    if (bgColorHex) {
                        style.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: bgColorHex }
                        } as ExcelJS.FillPattern;
                    }
                    break;
                }
                case 'font-size': {
                    if (!style.font) style.font = {};
                    const size = parseInt(value);
                    if (!isNaN(size)) style.font.size = size;
                    break;
                }
                case 'text-align':
                    if (!style.alignment) style.alignment = {};
                    (style.alignment as ExcelJS.Alignment).horizontal = value as ExcelJS.Alignment['horizontal'];
                    break;
            }
        });

        return style;
    };

    const getFilterValueOptions = (): FilterValueOption[] => {
        if (!selectedReport || selectedReport.isStatic) return [];
        const selectedFilter = filterOptions.find((option) => option.displayName === selectedReport.filterCriteria);
        const fieldName = (selectedFilter?.fieldName || selectedReport.filterCriteria || '').toLowerCase();
        const displayName = (selectedFilter?.displayName || selectedReport.filterCriteria || '').toLowerCase();

        if (fieldName.includes('country') || displayName.includes('country')) {
            return countries.map(c => ({ id: c.id, name: c.name, value: c.name }));
        }
        if (fieldName.includes('airport') || fieldName.includes('site') || displayName.includes('airport') || displayName.includes('site')) {
            return airports.map(a => ({ id: a.id, name: a.name, value: a.name }));
        }
        if (fieldName.includes('customer') || displayName.includes('customer')) {
            return customers.map(c => ({ id: c.id, name: c.name, value: c.id }));
        }
        if (fieldName.includes('payment') || displayName.includes('payment')) {
            return paymentTypes;
        }
        if (fieldName.includes('international') || fieldName.includes('intl') || displayName.includes('international')) {
            return [
                { id: 'international', name: 'International', value: '1' },
                { id: 'domestic', name: 'Domestic', value: '0' },
                { id: 'empty', name: '<Empty>', value: null }
            ];
        }
        return [];
    };

    const isInternationalField = (name?: string | null) => {
        if (!name) return false;
        const normalized = name.toLowerCase();
        return normalized.includes('international') || normalized.includes('intl');
    };

    const formatInternationalIndicatorValue = (value: unknown): string => {
        if (value === null || value === undefined) return '';
        const normalized = String(value).trim().toLowerCase();
        if (normalized === '' || normalized === 'null' || normalized === 'undefined') {
            return '';
        }
        if (['false', '0', 'n', 'no', 'domestic'].includes(normalized)) {
            return 'Domestic';
        }
        return 'International';
    };

    const isBooleanLikeValue = (value: unknown) => {
        if (value === null || value === undefined) return false;
        const normalized = String(value).trim().toLowerCase();
        if (normalized === '' || normalized === 'null' || normalized === 'undefined') {
            return false;
        }
        return ['true', 'false', '0', '1', 'y', 'n', 'yes', 'no', 'domestic', 'international'].includes(normalized);
    };

    const isDateColumn = (column?: ColumnDefinition): boolean => {
        if (!column) return false;

        const columnType = (column.type || '').trim().toLowerCase();

        return columnType.includes('date') || columnType.includes('time');
    };

    const parseExcelDateValue = (value: unknown): Date | null => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return value;
        }

        if (typeof value !== 'string' && typeof value !== 'number') {
            return null;
        }

        if (typeof value === 'string') {
            const trimmedValue = value.trim();
            const isoDateOnlyMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (isoDateOnlyMatch) {
                const year = Number(isoDateOnlyMatch[1]);
                const month = Number(isoDateOnlyMatch[2]);
                const day = Number(isoDateOnlyMatch[3]);
                const parsedDate = new Date(Date.UTC(year, month - 1, day));

                if (
                    parsedDate.getUTCFullYear() === year &&
                    parsedDate.getUTCMonth() === month - 1 &&
                    parsedDate.getUTCDate() === day
                ) {
                    return parsedDate;
                }
            }

            const slashDateMatch = trimmedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (slashDateMatch) {
                const day = Number(slashDateMatch[1]);
                const month = Number(slashDateMatch[2]);
                const year = Number(slashDateMatch[3]);
                const parsedDate = new Date(Date.UTC(year, month - 1, day));

                if (
                    parsedDate.getUTCFullYear() === year &&
                    parsedDate.getUTCMonth() === month - 1 &&
                    parsedDate.getUTCDate() === day
                ) {
                    return parsedDate;
                }
            }
        }

        const parsed = dayjs(value);
        if (!parsed.isValid()) {
            return null;
        }

        return parsed.toDate();
    };

    const formatCellValueByColumn = (value: unknown, column?: ColumnDefinition): ExcelJS.CellValue => {
        if (column && isInternationalField(column.name)) {
            return formatInternationalIndicatorValue(value);
        }
        if (isDateColumn(column)) {
            return parseExcelDateValue(value) || String(value ?? '');
        }
        return (value ?? '') as ExcelJS.CellValue;
    };

    const getDateTypeDisplayLabel = (value?: string | null): string => {
        switch (value) {
            case 'TRANSACTION_DATE':
                return 'Transaction Date';
            case 'CREATED_ON_DATE':
                return 'Received Date';
            default:
                return value || '';
        }
    };

    const formatReportTitleDate = (value?: string | null): string => {
        if (!value) {
            return '';
        }

        const parsedDate = parseExcelDateValue(value);
        if (!parsedDate) {
            return value;
        }

        return dayjs(parsedDate).format('DD-MMM-YYYY');
    };

    const configuredInternationalGroupName = useMemo(() => {
        if (!selectedReport?.selectedGroups || selectedReport.selectedGroups.length === 0) {
            return '';
        }
        const match = selectedReport.selectedGroups.find((groupName) => isInternationalField(groupName));
        return match || '';
    }, [selectedReport]);

    const formatGroupCellValue = (group: GroupData): string => {
        const label = group.groupLabel || group.groupDisplayName || '';
        const shouldFormatAsInternational =
            isInternationalField(label) ||
            isInternationalField(group.group) ||
            (!!configuredInternationalGroupName && isBooleanLikeValue(group.group));

        if (shouldFormatAsInternational) {
            return formatInternationalIndicatorValue(group.group) || '';
        }

        return label || group.group || '';
    };

    const generateExcelFromReport = async (
        reportData: ReportResponse,
        defaults?: { dateType?: string; startDate?: string; endDate?: string }
    ) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Report');

        let currentRow = 1;

        const reportDateType = reportData.dateType ?? reportData.report.metadata.dateType ?? defaults?.dateType;
        const reportDateTypeDisplay = getDateTypeDisplayLabel(reportDateType);
        const reportStartDate = reportData.startDate ?? reportData.report.metadata.startDate ?? defaults?.startDate;
        const reportEndDate = reportData.endDate ?? reportData.report.metadata.endDate ?? defaults?.endDate;
        const formattedReportStartDate = formatReportTitleDate(reportStartDate);
        const formattedReportEndDate = formatReportTitleDate(reportEndDate);

        // Title Row
        worksheet.mergeCells(currentRow, 1, currentRow, reportData.report.data.columns.length);
        const titleCell = worksheet.getCell(currentRow, 1);
        titleCell.value = reportData.report.metadata.title;
        titleCell.font = { bold: true, size: 18 };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        titleCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF8F9FA' }
        };
        currentRow += 1;

        // Report Based On Row
        if (reportDateTypeDisplay && reportStartDate && reportEndDate) {
            worksheet.mergeCells(currentRow, 1, currentRow, reportData.report.data.columns.length);
            const basedOnCell = worksheet.getCell(currentRow, 1);
            basedOnCell.value = `Report Based On ${reportDateTypeDisplay} : ${formattedReportStartDate} - ${formattedReportEndDate}`;
            basedOnCell.alignment = { horizontal: 'left', vertical: 'middle' };
            currentRow += 1;
        }

        currentRow += 1; // Empty row

        // Filter Row
        worksheet.mergeCells(currentRow, 1, currentRow, reportData.report.data.columns.length);
        const filterCell = worksheet.getCell(currentRow, 1);
        filterCell.value = reportData.report.metadata.filter;
        filterCell.alignment = { horizontal: 'left', vertical: 'middle' };
        filterCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF8F9FA' }
        };
        
        // Apply filter style if provided
        if (reportData.report.metadata.filterStyle) {
            const filterStyle = parseStyleString(reportData.report.metadata.filterStyle);
            if (filterStyle.font) filterCell.font = { ...filterCell.font, ...filterStyle.font };
            if (filterStyle.fill) filterCell.fill = filterStyle.fill;
            if (filterStyle.alignment) filterCell.alignment = { ...filterCell.alignment, ...filterStyle.alignment };
        }
        currentRow += 1; // Single empty row before groups

        const addColumnHeaders = () => {
            reportData.report.data.columns.forEach((column, index) => {
                const cell = worksheet.getCell(currentRow, index + 1);
                cell.value = column.name;
                cell.font = { bold: true };
                cell.alignment = { vertical: 'middle', wrapText: true };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE9ECEF' }
                };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
                
                // Apply column style if provided
                if (column.style) {
                    const columnStyle = parseStyleString(column.style);
                    if (columnStyle.font) cell.font = { ...cell.font, ...columnStyle.font };
                    if (columnStyle.fill) cell.fill = columnStyle.fill;
                    if (columnStyle.alignment) cell.alignment = { ...cell.alignment, ...columnStyle.alignment };
                }
                cell.alignment = { ...cell.alignment, wrapText: true };
            });
            currentRow++;
        };

        // Process each group
        const groups = Array.isArray(reportData.report.data.groups) ? reportData.report.data.groups : [];
        const isMeaningfulLabel = (value?: string | null) => {
            if (!value) return false;
            const trimmed = value.trim();
            if (!trimmed) return false;
            return trimmed.toLowerCase() !== 'ungrouped';
        };

        const validGroups = groups.filter(group => {
            const hasRows = group.rows && group.rows.length > 0;
            const labelCandidates = [group.group, group.groupLabel, group.groupDisplayName];
            const hasGroupLabel = labelCandidates.some(candidate => isMeaningfulLabel(candidate));
            return hasRows && hasGroupLabel;
        });

        if (validGroups.length === 0) {
            addColumnHeaders();
            groups
                .filter(group => group.rows && group.rows.length > 0)
                .forEach(group => {
                    group.rows.forEach((row, rowIndex) => {
                        row.forEach((cellValue, colIndex) => {
                            const cell = worksheet.getCell(currentRow, colIndex + 1);
                            const columnDef = reportData.report.data.columns[colIndex];
                            const formattedValue = formatCellValueByColumn(cellValue, columnDef);
                            cell.value = formattedValue;
                            cell.alignment = { vertical: 'top', wrapText: true };
                            if (formattedValue instanceof Date) {
                                cell.numFmt = 'dd-mmm-yyyy';
                            }
                            cell.border = {
                                top: { style: 'thin' },
                                left: { style: 'thin' },
                                bottom: { style: 'thin' },
                                right: { style: 'thin' }
                            };
                            const rowStyleEntry = group.rowStyles?.find(rs => rs.rowIndex === rowIndex);
                            if (rowStyleEntry?.style) {
                                const parsedRowStyle = parseStyleString(rowStyleEntry.style);
                                if (parsedRowStyle.font) cell.font = parsedRowStyle.font;
                                if (parsedRowStyle.fill) cell.fill = parsedRowStyle.fill as ExcelJS.FillPattern;
                                if (parsedRowStyle.alignment) cell.alignment = { ...cell.alignment, ...parsedRowStyle.alignment };
                            }
                            cell.alignment = { ...cell.alignment, wrapText: true };
                        });
                        currentRow++;
                    });
                });
        } else {
            validGroups.forEach((group, groupIndex) => {
                // Group header row
                if (group.group) {
                    worksheet.mergeCells(currentRow, 1, currentRow, reportData.report.data.columns.length);
                    const groupCell = worksheet.getCell(currentRow, 1);
                    groupCell.value = formatGroupCellValue(group);
                    groupCell.font = { bold: true };
                    if (group.groupStyle) {
                        const groupStyle = parseStyleString(group.groupStyle);
                        if (groupStyle.font) groupCell.font = { ...groupCell.font, ...groupStyle.font };
                        if (groupStyle.fill) groupCell.fill = groupStyle.fill as ExcelJS.FillPattern;
                    }
                    currentRow++;
                }

            // Column headers for this group
            addColumnHeaders();

                // Data rows
                group.rows.forEach((row, rowIndex) => {
                    row.forEach((cellValue, colIndex) => {
                        const cell = worksheet.getCell(currentRow, colIndex + 1);
                        const columnDef = reportData.report.data.columns[colIndex];
                        const formattedValue = formatCellValueByColumn(cellValue, columnDef);
                        cell.value = formattedValue;
                        cell.alignment = { vertical: 'top', wrapText: true };
                        if (formattedValue instanceof Date) {
                            cell.numFmt = 'dd-mmm-yyyy';
                        }
                        cell.border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' }
                        };
                        const rowStyleEntry = group.rowStyles?.find(rs => rs.rowIndex === rowIndex);
                        if (rowStyleEntry?.style) {
                            const parsedRowStyle = parseStyleString(rowStyleEntry.style);
                            if (parsedRowStyle.font) cell.font = parsedRowStyle.font;
                            if (parsedRowStyle.fill) cell.fill = parsedRowStyle.fill as ExcelJS.FillPattern;
                            if (parsedRowStyle.alignment) cell.alignment = { ...cell.alignment, ...parsedRowStyle.alignment };
                        }
                        cell.alignment = { ...cell.alignment, wrapText: true };
                    });
                    currentRow++;
                });

                // Empty row between groups
                if (groupIndex < validGroups.length - 1) {
                    currentRow++;
                }
            });
        }

        // Auto-fit columns so long values remain readable without blowing up layout
        reportData.report.data.columns.forEach((columnDefinition, index) => {
            const column = worksheet.getColumn(index + 1);
            let maxLength = columnDefinition?.name?.length ?? 0;
            column.eachCell({ includeEmpty: true }, (cell) => {
                const value = cell.value;
                if (value === null || value === undefined) return;

                let cellText = '';
                if (typeof value === 'object') {
                    if ('text' in (value as ExcelJS.CellValue) && typeof (value as { text?: unknown }).text === 'string') {
                        cellText = String((value as { text?: string }).text);
                    } else if ('richText' in (value as ExcelJS.CellValue) && Array.isArray((value as { richText?: { text: string }[] }).richText)) {
                        cellText = (value as { richText?: { text: string }[] }).richText?.map((entry) => entry.text).join('') || '';
                    } else {
                        cellText = String(value);
                    }
                } else {
                    cellText = String(value);
                }

                const lines = cellText.split(/\r?\n/);
                lines.forEach((line) => {
                    maxLength = Math.max(maxLength, line.length);
                });
            });
            if (maxLength === 0) {
                column.width = 14;
                return;
            }
            const adjustedWidth = Math.ceil(maxLength * 1.05) + 2;
            column.width = Math.min(Math.max(adjustedWidth, 14), 20);
        });

        let fileName = reportData.report.metadata.file || 'report.xlsx';
        if (fileName.endsWith('.xml')) {
            fileName = fileName.replace('.xml', '.xlsx');
        } else if (!fileName.endsWith('.xlsx')) {
            fileName += '.xlsx';
        }

        // Generate and download the file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    const handleStartDateChange = (date: SinglePickerValueType) => {
        setStartDate(date);
        if (!date || !endDate) return;

        const start = dayjs(date);
        const end = dayjs(endDate);
        const maxEnd = start.add(365, 'day');

        if (end.isBefore(start, 'day') || end.isAfter(maxEnd, 'day')) {
            setEndDate(null);
        }
    };

    const isStartDateDisabled = (current: dayjs.Dayjs | null) => {
        if (!current) return false;
        const today = dayjs().endOf('day');
        return current.isAfter(today, 'day');
    };

    const isEndDateDisabled = (current: dayjs.Dayjs | null) => {
        if (!current) return false;
        const today = dayjs().endOf('day');
        const start = startDate ? dayjs(startDate).startOf('day') : null;
        const maxRangeEnd = start ? start.add(365, 'day').endOf('day') : null;
        const effectiveMax = maxRangeEnd && maxRangeEnd.isBefore(today) ? maxRangeEnd : today;
        const beforeStart = start ? current.isBefore(start, 'day') : false;
        return current.isAfter(effectiveMax, 'day') || beforeStart;
    };

    const validateForm = () => {
        if (!selectedReportId) {
            toast.error('Please select a report');
            return false;
        }

        if (isFilterValueRequired && filterCriteriaValue.length === 0) {
            toast.error('Filter value is required');
            return false;
        }
        if (!dateTimeType) {
            toast.error('Date Time is required');
            return false;
        }
        if (!startDate) {
            toast.error('Start date is required');
            return false;
        }
        const today = dayjs().endOf('day');
        if (dayjs(startDate).isAfter(today, 'day')) {
            toast.error('Start date cannot be later than today');
            return false;
        }
        if (!endDate) {
            toast.error('End date is required');
            return false;
        }
        const start = dayjs(startDate).startOf('day');
        const end = dayjs(endDate).startOf('day');
        if (end.isAfter(today, 'day')) {
            toast.error('End date cannot be later than today');
            return false;
        }
        if (end.isBefore(start)) {
            toast.error('End date cannot be earlier than start date');
            return false;
        }
        if (end.diff(start, 'day') > 365) {
            toast.error('Date range cannot exceed 365 days');
            return false;
        }
        return true;
    };

    const handleRunReport = () => {
        if (isGeneratingReport || !validateForm()) {
            return;
        }

        setIsGeneratingReport(true);

        const isStaticReport = Boolean(selectedReport?.isStatic);
        const selectedFilter = !isStaticReport
            ? filterOptions.find((option) => option.displayName === selectedReport?.filterCriteria)
            : undefined;
        const filterCriteriaId = isStaticReport ? null : (selectedFilter?.id ?? selectedReport?.filterCriteriaId ?? null);
        const filterValuePayload = isStaticReport ? null : serializeListField(filterCriteriaValue);
        const filterFieldName = isStaticReport
            ? null
            : (selectedFilter?.fieldName || selectedReport?.filterCriteria || '');
        const requestData = {
            reportId: selectedReportId,
            reportName: selectedReport?.reportName || '',
            filterCriteriaId: filterCriteriaId !== null ? String(filterCriteriaId) : null,
            filterCriteria: filterFieldName,
            filterValue: filterValuePayload,
            dateType: dateTimeType,
            startDate: dayjs(startDate).format('YYYY-MM-DD'),
            endDate: dayjs(endDate).format('YYYY-MM-DD'),
            isPassthroughIncluded: passthroughIncluded,
            isStatic: isStaticReport,
            staticReportId: selectedReport?.staticReportId ?? null
        };

        postData(ApiUrls.runReport, requestData)
            .then((response) => {
                const reportData = response.data?.data ?? response.data;
                if (reportData?.report) {
                    generateExcelFromReport(reportData, {
                        dateType: dateTimeType,
                        startDate: dayjs(startDate).format('YYYY-MM-DD'),
                        endDate: dayjs(endDate).format('YYYY-MM-DD')
                    });
                    toast.success('Report downloaded successfully');
                    return;
                }
                toast.error('No report data returned');
            })
            .catch((error) => {
                console.error('Failed to generate report:', error);
                toast.error('Failed to generate report');
            })
            .finally(() => {
                setIsGeneratingReport(false);
            });
    };

    return (
        <ContainerDiv className="report-container">
            <Card
                className="report-card"
                style={{border: '2px solid #d9d9d9', borderRadius: '8px', padding: '24px', paddingBottom: '120px', position: 'relative', overflow: 'visible'}}
            >
                {isGeneratingReport && <Loader />}
                <div style={{marginBottom: '24px'}}>
                    <Heading level={2} className="report-heading" style={{margin: 0, fontWeight: 700}}>
                        Generate Report
                    </Heading>
                </div>
                
                <Grid columns={2} gap='4%'>
                    <div>
                        <Grid columns="30% 70%" style={{width:"95%"}}>
                            
                            <Col size="100"><Label bold type={TextTypes.Label} htmlFor='reports'>Reports <span style={{ color: 'red' }}>*</span></Label></Col>
                            <Col size="100">
                                <Select 
                                    id='reports'
                                    placeholder="Select report"
                                    value={selectedReportId}
                                    size="medium"
                                    showSearch 
                                    style={{ width: '100%' }}
                                    onChange={(value) => setSelectedReportId(value)}
                                >
                                    {reports.map((report) => (
                                        <Option key={report.id} value={report.id}>
                                            {report.reportName}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>

                            {shouldShowFilterFields && selectedReport && (
                                <>
                                    <Col size="100"><Label bold type={TextTypes.Label} htmlFor='filterCriteria'>Filter Criteria</Label></Col>
                                    <Col size="100">
                                        <Select
                                            id='filterCriteria'
                                            value={selectedReport.filterCriteria}
                                            disabled
                                            style={{ width: '100%' }}
                                        >
                                            <Option value={selectedReport.filterCriteria}>
                                                {selectedReport.filterCriteria}
                                            </Option>
                                        </Select>
                                    </Col>

                                    <Col size="100">
                                        <Label bold type={TextTypes.Label} htmlFor='filterValue'>
                                            Filter Value {isFilterValueRequired && <span style={{ color: 'red' }}>*</span>}
                                        </Label>
                                    </Col>
                                    <Col size="100">
                                        <Select
                                            id='filterValue'
                                            mode="multiple"
                                            value={filterCriteriaValue}
                                            onChange={(value) => setFilterCriteriaValue(Array.isArray(value) ? (value as string[]) : [])}
                                            placeholder={`Select ${selectedReport.filterCriteria.toLowerCase()}`}
                                            showSearch
                                            loading={isLoadingFilters}
                                            style={{ width: '100%' }}
                                        >
                                            {getFilterValueOptions().map((option) => (
                                                <Option
                                                    key={`${option.id}-${option.value ?? option.name}`}
                                                    value={option.value ?? option.name}
                                                >
                                                    {option.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Col>
                                </>
                            )}

                        </Grid>
                    </div>

                    <div>
                        <Grid columns="30% 70%" style={{width:"95%"}}>
                            
                            <Col size="100">
                                <Label bold type={TextTypes.Label} htmlFor='dateType'>
                                    Date Type <span style={{ color: 'red' }}>*</span>
                                </Label>
                            </Col>
                            <Col size="100">
                                <Select
                                    id='dateType'
                                    value={dateTimeType}
                                    onChange={(value) => setDateTimeType(value)}
                                    placeholder="Select date type"
                                    style={{ width: '100%' }}
                                >
                                    <Option value="TRANSACTION_DATE">Transaction Date</Option>
                                    <Option value="CREATED_ON_DATE">Received Date</Option>
                                </Select>
                            </Col>
                            
                            <Col size="100"><Label bold type={TextTypes.Label} htmlFor='startDate'>Start Date <span style={{ color: 'red' }}>*</span></Label></Col>
                            <Col size="100">
                                <SingleDatePicker
                                    id='startDate'
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    placeholder="Select start date"
                                    style={{ width: '100%' }}
                                    format="DD-MMM-YYYY"
                                    disabledDate={isStartDateDisabled}
                                    size="small"
                                    showToday={false}
                                    placement="bottomLeft"
                                />
                            </Col>

                            <Col size="100"><Label bold type={TextTypes.Label} htmlFor='endDate'>End Date <span style={{ color: 'red' }}>*</span></Label></Col>
                            <Col size="100">
                                <SingleDatePicker
                                    id='endDate'
                                    value={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    placeholder="Select end date"
                                    style={{ width: '100%' }}
                                    format="DD-MMM-YYYY"
                                    disabledDate={isEndDateDisabled}
                                    size="small"
                                    showToday={false}
                                    placement="bottomLeft"
                                    defaultPickerValue={endPickerDefaultValue}
                                />
                            </Col>

                            <Col size="100"><Label bold type={TextTypes.Label} htmlFor='passthroughIncluded'>Passthrough Included?</Label></Col>
                            <Col size="100">
                                <Toggle 
                                    id='passthroughIncluded'
                                    checked={passthroughIncluded} 
                                    onChange={(value: boolean) => setPassthroughIncluded(value)} 
                                />
                            </Col>

                        </Grid>
                    </div>
                </Grid>

                <div style={{marginTop: '32px', display: 'flex', gap: '16px', justifyContent: 'flex-start'}}>
                    <Button 
                        variant={Variants.Filled}
                        onClick={handleRunReport}
                        disabled={
                            isGeneratingReport ||
                            !selectedReportId ||
                            (isFilterValueRequired && filterCriteriaValue.length === 0) ||
                            !dateTimeType ||
                            !startDate ||
                            !endDate
                        }
                    >
                        Run Report
                    </Button>
                </div>
            </Card>
        </ContainerDiv>
    );
};

export default GenerateReport;
